import { useCallback, useEffect, useRef, useState } from 'react';

type Status = 'idle' | 'loading' | 'success' | 'error';

interface UseAsyncState<T> {
  data: T | null;
  status: Status;
  error: string | null;
  refetch: () => void;
}

/**
 * Simple async data fetcher hook.
 * Stores fn in a ref to avoid dependency churn from inline lambdas.
 * tick increments trigger re-execution (used by refetch).
 */
export function useAsync<T>(fn: () => Promise<T>): UseAsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // Ref-based stable fn — callers pass new lambdas each render; we pin to latest
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: tick is intentional refetch trigger
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fnRef
      .current()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setStatus('success');
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { data, status, error, refetch };
}
